/* =====================================================================
 * @auw/tenant — parseTenantManifest()
 *
 * Validates an untyped manifest object (imported TS module, parsed
 * JSON, or `/config.json` runtime overlay) and returns a typed
 * `TenantManifest`.  Throws a descriptive Error on failure — callers
 * should let this propagate at build time so misconfigured deployments
 * never make it into production.
 * ===================================================================== */

import { TenantManifestSchema } from './schema';
import type {
  TenantAnalytics,
  TenantAuth,
  TenantManifest,
  TenantPayments,
  TenantPlugins,
  TenantRoles,
  TenantSpecializedAgent,
  TenantWorkspace,
} from './types';
import type { FeatureFlagMap } from '@auw/feature-flags';

export function parseTenantManifest(raw: unknown): TenantManifest {
  const parsed = TenantManifestSchema.safeParse(raw);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((issue) => `  - ${issue.path.join('.') || '<root>'}: ${issue.message}`)
      .join('\n');
    throw new Error(
      `[tenant] Invalid TenantManifest:\n${issues || '<unknown validation failure>'}`,
    );
  }
  // The schema keeps `features` permissive (record<string, boolean>) so
  // it accepts future FeatureId additions without a schema rev; narrow
  // to the typed `FeatureFlagMap` here for consumers.
  //
  // The same goes for `workspace.modeOrder` — the schema validates the
  // shape (array of non-empty strings) but does not enforce the
  // `WorkspaceMode` union so new modes in `@auw/app-shell` don't force
  // every manifest to republish. Resolver helpers (`workspace.ts`)
  // filter unknown ids at consumption time via `isWorkspaceMode`.
  //
  // `plugins.hiddenIds` / `plugins.order` / `plugins.overrides` are
  // likewise string-typed at the schema level — enterprise tenants
  // may ship custom MCP connectors whose ids are not in the builtin
  // descriptor registry, and `resolvePluginCatalog()` reconciles the
  // two sides at runtime.
  //
  // `auth.providers[].kind` is the one exception: it's a closed
  // enum here because `@auw/auth` owns the adapter factory table,
  // and adding a new kind is a deliberate cross-package change.
  //
  // `roles` follows the same permissive pattern: permission strings
  // stay opaque at schema time so enterprise tenants can mint new
  // permissions scoped to their own admin packs. `@auw/permissions`
  // evaluates them as plain string equality.
  //
  // `analytics` / `payments` carry a closed boolean `enabled` flag plus
  // optional provider-specific string overrides. The schema narrows the
  // shape but the nested `sentry.allowUrls` list is kept permissive so
  // future patterns (e.g. capturing a CI preview domain) can be added
  // without rev'ing the schema.
  const {
    features,
    workspace,
    plugins,
    auth,
    roles,
    analytics,
    payments,
    specializedAgents,
    ...rest
  } = parsed.data;
  return {
    ...rest,
    features: features as FeatureFlagMap,
    workspace: workspace as TenantWorkspace | undefined,
    plugins: plugins as TenantPlugins | undefined,
    auth: auth as TenantAuth | undefined,
    roles: roles as TenantRoles | undefined,
    analytics: analytics as TenantAnalytics | undefined,
    payments: payments as TenantPayments | undefined,
    specializedAgents: specializedAgents as readonly TenantSpecializedAgent[] | undefined,
  };
}
